import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { patchSafely, type FormEdit } from '../../lib/safeSave';
import { toast } from '../../lib/toast';
import { api } from '../../lib/api';
import type { Material, StockMovement } from './types';
import type { MaterialFormValues } from './schema';

const KEY = ['materials'] as const;
const movementsKey = (id: string) => ['materials', id, 'movements'] as const;

function toPayload(v: MaterialFormValues) {
  const opt = (s: string) => (s.trim() === '' ? undefined : s.trim());
  const num = (s: string) => (s.trim() === '' ? undefined : Number(s.trim().replace(',', '.')));
  return {
    sku: v.sku.trim(),
    name: v.name.trim(),
    category: opt(v.category),
    unit: v.unit.trim(),
    stockQty: num(v.stockQty) ?? 0,
    minQty: num(v.minQty),
    pricePerUnit: num(v.pricePerUnit),
    currency: v.currency.trim().toUpperCase(),
    supplier: opt(v.supplier),
    supplierUrl: opt(v.supplierUrl),
    location: opt(v.location),
    notes: opt(v.notes),
    // Prázdné = odebrat (backend převede '' na null).
    barcode: v.barcode.trim(),
    locationId: v.locationId,
  };
}

// Výběry dostanou jen aktivní materiál; stránka Sklad i archivovaný.
export function useMaterials(opts: { includeArchived?: boolean } = {}) {
  return useQuery({
    queryKey: opts.includeArchived ? [...KEY, 'vcetne-archivu'] : KEY,
    queryFn: () => api.get<Material[]>(opts.includeArchived ? '/api/materials?archiv=1' : '/api/materials'),
  });
}

export function useArchiveMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api.post<Material>(`/api/materials/${id}/archive`, { archived }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useCreateMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: MaterialFormValues) => api.post<Material>('/api/materials', toPayload(values)),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values, edit }: { id: string; values: MaterialFormValues; edit?: FormEdit<MaterialFormValues> }) =>
      patchSafely<Material>(`/api/materials/${id}`, toPayload(values), edit && { start: toPayload({ ...values, ...edit.start }), record: edit.record }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/api/materials/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// --- Pohyby skladu (ocenění) ---

export function useMaterialMovements(id: string, enabled = true) {
  return useQuery({
    queryKey: movementsKey(id),
    queryFn: () => api.get<StockMovement[]>(`/api/materials/${id}/movements`),
    enabled,
  });
}

function useStockMutation<T>(id: string, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: movementsKey(id) });
    },
  });
}

export interface ReceiveInput {
  qty: number;
  unitCost: number;
  note?: string;
}

// Naskladnění (nákup) — přepočítá vážený průměr.
export function useReceiveMaterial(id: string) {
  return useStockMutation(id, (data: ReceiveInput) => api.post<Material>(`/api/materials/${id}/receipts`, data));
}

// Korekce / inventura — nastaví absolutní stav.
export function useAdjustMaterial(id: string) {
  return useStockMutation(id, (data: { newQty: number; note?: string }) =>
    api.post<Material>(`/api/materials/${id}/adjust`, data),
  );
}

// Odpis znehodnoceného materiálu (prošlé, poškozené) — ztráta studia, ne zakázky.
export function useScrapMaterial(id: string) {
  return useStockMutation(id, (data: { qty: number; reason: string }) =>
    api.post<Material>(`/api/materials/${id}/scrap`, data),
  );
}

// Štítky na regály (QR + SKU + název + umístění).
export const materialLabelsUrl = (ids?: string[], size: 'small' | 'std' = 'small') =>
  `/api/materials/labels.pdf?size=${size}${ids && ids.length ? `&ids=${ids.join(',')}` : ''}`;

// Vyhledání podle SKU (QR sken / čtečka).
export const findMaterialBySku = (sku: string) =>
  api.get<Material>(`/api/materials/by-sku/${encodeURIComponent(sku)}`);

// --- Spotřeba materiálu na fázi (odečet ze skladu + náklad do fáze) ---

export interface PhaseMaterialUsage {
  id: string;
  materialId: string;
  quantityActual: string;
  wastedQty: string;
  costAtTime: string | null; // null bez oprávnění finance
  currency: string;
  notes: string | null;
  deductedAt: string | null;
  createdAt: string;
  material: { name: string; sku: string; unit: string };
}

const phaseMaterialsKey = (phaseId: string) => ['phases', phaseId, 'materials'] as const;

export function usePhaseMaterials(phaseId: string) {
  return useQuery({
    queryKey: phaseMaterialsKey(phaseId),
    queryFn: () => api.get<PhaseMaterialUsage[]>(`/api/phases/${phaseId}/materials`),
  });
}

function usePhaseMaterialMutation<T>(phaseId: string, propItemId: string, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: phaseMaterialsKey(phaseId) });
      qc.invalidateQueries({ queryKey: ['prop-items', propItemId, 'phases'] });
      qc.invalidateQueries({ queryKey: ['prop-economics', propItemId] });
      qc.invalidateQueries({ queryKey: ['prop-materials', propItemId] });
      qc.invalidateQueries({ queryKey: KEY }); // stav skladu se změnil
      qc.invalidateQueries({ queryKey: ['phase-reservations', phaseId] }); // spotřeba zmenšuje rezervaci
    },
  });
}

// --- Rezervace materiálu na fázi (plánovaná výroba) ---
export interface PhaseReservation {
  id: string;
  materialId: string;
  quantity: number;
  consumed: number; // kolik se z materiálu na fázi už spotřebovalo
  note: string | null;
  material: { name: string; sku: string; unit: string };
}

const phaseReservationsKey = (phaseId: string) => ['phase-reservations', phaseId] as const;

export function usePhaseReservations(phaseId: string) {
  return useQuery({
    queryKey: phaseReservationsKey(phaseId),
    queryFn: () => api.get<PhaseReservation[]>(`/api/phases/${phaseId}/reservations`),
  });
}

function useReservationMutation<T>(phaseId: string, fn: (v: T) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: phaseReservationsKey(phaseId) });
      qc.invalidateQueries({ queryKey: KEY }); // dostupné množství na skladě
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useAddReservation(phaseId: string) {
  return useReservationMutation(phaseId, async (data: { materialId: string; quantity: number; note?: string }) => {
    const saved = await api.post<{ shortage?: number }>(`/api/phases/${phaseId}/reservations`, data);
    if (saved.shortage) toast.error(`Rezervováno, ale na skladě chybí ${saved.shortage}. Doobjednej, nebo přeplánuj.`);
    return saved;
  });
}

export function useRemoveReservation(phaseId: string) {
  return useReservationMutation(phaseId, (id: string) => api.del(`/api/material-reservations/${id}`));
}

export interface AddPhaseMaterialInput {
  materialId: string;
  quantity: number;
  wastedQty?: number; // kolik z toho byl odpad (odřezky, zmetek)
  note?: string;
}

export function useAddPhaseMaterial(phaseId: string, propItemId: string) {
  return usePhaseMaterialMutation(phaseId, propItemId, (data: AddPhaseMaterialInput) =>
    api.post<PhaseMaterialUsage>(`/api/phases/${phaseId}/materials`, data),
  );
}

export function useMovePhaseMaterial(phaseId: string, propItemId: string) {
  return usePhaseMaterialMutation(phaseId, propItemId, ({ id, to }: { id: string; to: string }) => api.post(`/api/material-usages/${id}/move`, { phaseId: to }));
}

export function useRemovePhaseMaterial(phaseId: string, propItemId: string) {
  return usePhaseMaterialMutation(phaseId, propItemId, (id: string) => api.del(`/api/material-usages/${id}`));
}
